/**
 * ContractEstimateSlice Procedure Code Reducers
 * 
 * Reducers for managing procedure codes within an estimate.
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { ContractEstimateSliceState } from '../state';
import {
  ProcedureCodeEstimate,
  CreateProcedureCodePayload,
  UpdateProcedureCodePayload,
  ContractEstimate,
} from '../../types/contractEstimateTypes';

/**
 * Calculate totals for an estimate
 */
const calculateTotals = (estimate: ContractEstimate): ContractEstimate => {
  const totalReimbursement = estimate.procedureCodes.reduce(
    (sum, proc) => sum + proc.reimbursement * proc.units,
    0
  );
  const totalImplantCost = estimate.procedureCodes.reduce(
    (sum, proc) => sum + proc.implantCost,
    0
  );
  const grandTotal = totalReimbursement + totalImplantCost;

  return {
    ...estimate,
    totalReimbursement,
    totalImplantCost,
    grandTotal,
    lastModified: new Date().toISOString(),
  };
};

/**
 * Add a procedure code to the current estimate
 */
export const addProcedureCode = (
  state: ContractEstimateSliceState,
  action: PayloadAction<CreateProcedureCodePayload>
) => {
  if (state.currentEstimate) {
    const reimbursement = action.payload.reimbursement ?? 0;
    const newProcedure: ProcedureCodeEstimate = {
      id: crypto.randomUUID(),
      ...action.payload,
      reimbursement,
      totalCost: reimbursement * action.payload.units + action.payload.implantCost,
    };

    state.currentEstimate.procedureCodes.push(newProcedure);
    state.currentEstimate = calculateTotals(state.currentEstimate);
    state.isDirty = true;
  }
};

/**
 * Update a procedure code in the current estimate
 */
export const updateProcedureCode = (
  state: ContractEstimateSliceState,
  action: PayloadAction<UpdateProcedureCodePayload>
) => {
  if (state.currentEstimate) {
    const index = state.currentEstimate.procedureCodes.findIndex(
      (proc) => proc.id === action.payload.id
    );
    if (index !== -1) {
      const updatedProcedure = {
        ...state.currentEstimate.procedureCodes[index],
        ...action.payload.updates,
      };
      updatedProcedure.totalCost =
        updatedProcedure.reimbursement * updatedProcedure.units +
        updatedProcedure.implantCost;

      state.currentEstimate.procedureCodes[index] = updatedProcedure;
      state.currentEstimate = calculateTotals(state.currentEstimate);
      state.isDirty = true;
    }
  }
};

/**
 * Remove a procedure code from the current estimate
 */
export const removeProcedureCode = (
  state: ContractEstimateSliceState,
  action: PayloadAction<string>
) => {
  if (state.currentEstimate) {
    state.currentEstimate.procedureCodes = state.currentEstimate.procedureCodes.filter(
      (proc) => proc.id !== action.payload
    );
    state.currentEstimate = calculateTotals(state.currentEstimate);
    state.isDirty = true;
  }
};

/**
 * Reorder procedure codes in the current estimate
 */
export const reorderProcedureCodes = (
  state: ContractEstimateSliceState,
  action: PayloadAction<ProcedureCodeEstimate[]>
) => {
  if (state.currentEstimate) {
    state.currentEstimate.procedureCodes = action.payload;
    state.currentEstimate.lastModified = new Date().toISOString();
    state.isDirty = true;
  }
};

/**
 * Clear all procedure codes and calculation results from the current estimate
 */
export const clearProcedureCodes = (state: ContractEstimateSliceState) => {
  if (state.currentEstimate) {
    state.currentEstimate.procedureCodes = [];
    state.currentEstimate.calculationResult = undefined;
    state.currentEstimate.totalReimbursement = 0;
    state.currentEstimate.totalImplantCost = 0;
    state.currentEstimate.grandTotal = 0;
    state.currentEstimate.lastModified = new Date().toISOString();
    state.isDirty = true;
  }
};
